import type { ReactNode, MouseEvent, AnimationEvent, HTMLAttributes } from 'react'
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import { sc } from 'utils'
import Spinner from 'components/Spinner'
import { CrossMediumIcon } from 'icons'

let modalRoot = document.getElementById('modal-root')

if (modalRoot === null) {
  modalRoot = document.createElement('div')
  modalRoot.id = 'modal-root'
  document.body.appendChild(modalRoot)
}

type Props = {
  isOpen: boolean
  loading?: boolean
  children: ReactNode
  onClose: () => void
} & HTMLAttributes<HTMLDivElement>

export default function Modal({
  isOpen = false,
  loading = false,
  children,
  onClose,
  ...props
}: Props) {
  const [shouldRender, setShouldRender] = useState(isOpen)

  const handleBackgroundClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose()
  }

  const onAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && !isOpen) setShouldRender(false)
  }

  useEffect(() => {
    if (isOpen) setShouldRender(true)
  }, [isOpen])

  if (!modalRoot || !shouldRender) return null

  return createPortal(
    <Background
      role="dialog"
      onClick={handleBackgroundClick}
      onAnimationEnd={onAnimationEnd}
      isOpen={isOpen}
    >
      {loading && <Spinner size={60} style={{ color: 'white' }} />}

      {!loading && (
        <Content isOpen={isOpen} {...props}>
          <CloseButton onClick={onClose}>
            <CrossMediumIcon />
          </CloseButton>

          {children}
        </Content>
      )}
    </Background>,
    modalRoot
  )
}

const backgroundEnter = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const backgroundExit = keyframes`
  from { opacity: 1; }
  to { opacity: 0;}
`

const modalEnter = keyframes`
  from { transform: scale(1.05); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
 `

const modalExit = keyframes`
  from { transform: scale(1); }
  to { transform: scale(0.95); }
`

const Background = styled.div<{ isOpen: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  background: rgba(0, 0, 0, 0.4);

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;

  animation-name: ${sc('isOpen', backgroundEnter, backgroundExit)};
  animation-duration: 0.2s;
  animation-fill-mode: forwards;
`

const Content = styled.div<{ isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  width: fit-content;
  max-height: calc(100% - 16px);
  margin: 8px;
  padding: 24px;

  background: var(--white);
  border-radius: 8px;

  font: var(--400-15);
  text-align: center;

  overflow: auto;

  animation-name: ${sc('isOpen', modalEnter, modalExit)};
  animation-duration: 0.2s;
  animation-fill-mode: ${sc('isOpen', 'backwards', 'forwards')};
  animation-delay: ${sc('isOpen', 0.1, 0)}s;

  h1 {
    font: var(--600-24);
  }

  strong {
    font: var(--600-16);
  }

  a {
    color: inherit;
  }
`

const CloseButton = styled.button.attrs({ type: 'button' })`
  flex: none;
  align-self: flex-end;
  padding: 2px;
  margin-bottom: -24px;
  background: none;
  border: none;
  cursor: pointer;

  :focus-visible {
    outline: 2px solid var(--green_0-5);
  }

  :hover:enabled:not(:focus-visible) svg {
    transform: rotate(90deg);
  }

  svg {
    display: block;
    transition: transform 0.2s;
  }
`
